import React from "react";
import {
    Select,
    MenuItem,
    Typography,
    TextField,
    Autocomplete,
    CircularProgress,
    Box,
} from "@mui/material";

// Reusable form field components
export const FormLabel = ({ children }) => (
    <Typography
        component="label"
        sx={{
            fontSize: "13px",
            fontWeight: 500,
            color: "#111827",
            marginBottom: "8px",
        }}
    >
        {children}
    </Typography>
);

export const FormSelect = ({ value, onChange, placeholder, options }) => (
    <Select
        value={value}
        onChange={onChange}
        displayEmpty
        sx={{
            height: "36px",
            fontSize: "14px",
            ".MuiOutlinedInput-notchedOutline": {
                borderColor: "#E5E7EB",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#E5E7EB",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#6366F1",
            },
            "& .MuiSelect-select": {
                fontSize: "14px",
            },
        }}
    >
        <MenuItem value="" disabled sx={{ fontSize: "14px" }}>
            {placeholder}
        </MenuItem>
        {options.map((option) => (
            <MenuItem
                key={option.value}
                value={option.value}
                sx={{ fontSize: "14px" }}
            >
                {option.label}
            </MenuItem>
        ))}
    </Select>
);

export const PropertySearch = ({
    value,
    onChange,
    onInputChange,
    loading,
    options,
}) => (
    <Autocomplete
        options={options}
        loading={loading}
        value={value}
        onChange={onChange}
        onInputChange={onInputChange}
        renderInput={(params) => (
            <TextField
                {...params}
                label="Search Property"
                placeholder="Search property"
                variant="outlined"
                size="small"
                sx={{
                    bgcolor: "#fff",
                    "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                            borderColor: "#E5E7EB",
                        },
                        "& input": {
                            fontSize: "14px",
                        },
                    },
                    "& .MuiInputLabel-root": {
                        fontSize: "14px",
                        "&.MuiInputLabel-shrink": {
                            transform: "translate(14px, -9px) scale(0.75)",
                            backgroundColor: "#fff",
                            padding: "0 4px",
                        },
                    },
                }}
            />
        )}
    />
);

export const EntitySelect = ({
    value,
    onChange,
    entityLabel,
    loading,
    options,
}) => (
    <Select
        value={value}
        onChange={onChange}
        displayEmpty
        sx={{
            height: "36px",
            fontSize: "14px",
            ".MuiOutlinedInput-notchedOutline": {
                borderColor: "#E5E7EB",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#E5E7EB",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#6366F1",
            },
            "& .MuiSelect-select": {
                fontSize: "14px",
            },
        }}
    >
        <MenuItem value="" disabled sx={{ fontSize: "14px" }}>
            {loading ? (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <CircularProgress size={16} />
                    <Typography variant="h6" sx={{ fontSize: "14px" }}>
                        Loading {entityLabel.toLowerCase()}...
                    </Typography>
                </Box>
            ) : (
                `Select ${entityLabel.toLowerCase()}`
            )}
        </MenuItem>
        {options.map((option) => (
            <MenuItem
                key={option.value}
                value={option.value}
                sx={{ fontSize: "14px" }}
            >
                {option.label}
            </MenuItem>
        ))}
    </Select>
);

// Common options lists
export const messageTypeOptions = [
    { value: "Property", label: "Property" },
    { value: "Rateplan", label: "Rateplan" },
    { value: "Roomtype", label: "Roomtype" },
    { value: "Taxes", label: "Taxes" },
    { value: "Cancellation Policy", label: "Cancellation Policy" },
    { value: "Products", label: "Products" },
    { value: "Offer", label: "Offer" },
];

export const updateTypeOptions = [
    { value: "new", label: "NEW" },
    { value: "enable", label: "ENABLE" },
    { value: "disable", label: "DISABLE" },
    { value: "update", label: "UPDATE" },
];

// Helper functions
const messageTypeMap = {
    Taxes: { objectType: "Tax", entityLabel: "Tax" },
    "Cancellation Policy": {
        objectType: "Policy",
        entityLabel: "Cancellation Policy",
    },
    Products: { objectType: "Product", entityLabel: "Rateplan" },
    Offer: { objectType: "Offer", entityLabel: "Rateplan" },
};

export const getObjectType = (messageType) => {
    return messageTypeMap[messageType]
        ? messageTypeMap[messageType].objectType
        : messageType;
};

export const getEntityLabel = (messageType) => {
    return messageTypeMap[messageType]
        ? messageTypeMap[messageType].entityLabel
        : messageType;
};

export const formatJsonData = (data) => {
    if (data) {
        try {
            const parsedData = JSON.parse(data);
            return JSON.stringify(parsedData, undefined, 4);
        } catch (err) {
            return data;
        }
    }
    return data;
};
